import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

export const useFurnizori = () => {
  const { token } = useStateContext();
  const [furnizori, setFurnizori] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const fetchFurnizori = useCallback(async (page = 1, pageSize = 50, filters = {}) => {
    if (!token) return;
    
    console.log('Fetching furnizori with params:', { page, pageSize, filters });
    
    setLoading(true);
    try {
      const params = {
        token,
        page,
        page_size: pageSize,
        ...filters
      };

      console.log('Request params:', params);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/furnizori`, { params });
      
      console.log('Furnizori response:', response.data);
      
      setFurnizori(response.data.items || []);
      setPagination({
        current: page,
        pageSize,
        total: response.data.total || 0
      });
    } catch (error) {
      console.error('Eroare la încărcarea furnizorilor:', error);
      message.error('Eroare la încărcarea furnizorilor');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createFurnizor = useCallback(async (furnizorData) => {
    if (!token) return false;
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/furnizori`, furnizorData, {
        params: { token }
      });
      
      message.success('Furnizorul a fost creat cu succes');
      fetchFurnizori(pagination.current, pagination.pageSize);
      return response.data;
    } catch (error) {
      console.error('Eroare la crearea furnizorului:', error);
      message.error('Eroare la crearea furnizorului');
      return false;
    }
  }, [token, pagination, fetchFurnizori]);

  const updateFurnizor = useCallback(async (id, furnizorData) => {
    if (!token) return false;
    
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/furnizori/${id}`, furnizorData, {
        params: { token }
      });
      
      message.success('Furnizorul a fost actualizat cu succes');
      fetchFurnizori(pagination.current, pagination.pageSize);
      return response.data;
    } catch (error) {
      console.error('Eroare la actualizarea furnizorului:', error);
      message.error('Eroare la actualizarea furnizorului');
      return false;
    }
  }, [token, pagination, fetchFurnizori]);

  const deleteFurnizor = useCallback(async (id) => {
    if (!token) return false;
    
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/furnizori/${id}`, {
        params: { token }
      });
      
      message.success('Furnizorul a fost șters cu succes');
      fetchFurnizori(pagination.current, pagination.pageSize);
      return true;
    } catch (error) {
      console.error('Eroare la ștergerea furnizorului:', error);
      message.error('Eroare la ștergerea furnizorului');
      return false;
    }
  }, [token, pagination, fetchFurnizori]);

  // Funcții pentru gestionarea legăturilor produs-furnizor
  const createProdusToFurnizorLink = useCallback(async (linkData) => {
    if (!token) return false;
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/produs-furnizor`, linkData, {
        params: { token }
      });
      
      message.success('Legătura a fost creată cu succes');
      return response.data;
    } catch (error) {
      console.error('Eroare la crearea legăturii:', error);
      message.error(error.response?.data?.detail || 'Eroare la crearea legăturii');
      return false;
    }
  }, [token]);

  const updateProdusToFurnizorLink = useCallback(async (linkId, updateData) => {
    if (!token) return false;
    
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/produs-furnizor/${linkId}`, updateData, {
        params: { token }
      });
      
      message.success('Legătura a fost actualizată cu succes');
      return response.data;
    } catch (error) {
      console.error('Eroare la actualizarea legăturii:', error);
      message.error(error.response?.data?.detail || 'Eroare la actualizarea legăturii');
      return false;
    }
  }, [token]);

  const deleteProdusToFurnizorLink = useCallback(async (linkId) => {
    if (!token) return false;
    
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/produs-furnizor/${linkId}`, {
        params: { token }
      });
      
      message.success('Legătura a fost ștearsă cu succes');
      return true;
    } catch (error) {
      console.error('Eroare la ștergerea legăturii:', error);
      message.error(error.response?.data?.detail || 'Eroare la ștergerea legăturii');
      return false;
    }
  }, [token]);

  const getFurnizoriForProdus = useCallback(async (produsId) => {
    if (!token) return [];
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/produs-furnizor/${produsId}`, {
        params: { token }
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Eroare la încărcarea furnizorilor pentru produs:', error);
      message.error('Eroare la încărcarea furnizorilor pentru produs');
      return [];
    }
  }, [token]);

  useEffect(() => {
    fetchFurnizori();
  }, [fetchFurnizori]);

  return {
    furnizori,
    loading,
    pagination,
    fetchFurnizori,
    createFurnizor,
    updateFurnizor,
    deleteFurnizor,
    // Funcții noi pentru legăturile produs-furnizor
    createProdusToFurnizorLink,
    updateProdusToFurnizorLink,
    deleteProdusToFurnizorLink,
    getFurnizoriForProdus
  };
}; 